import React from 'react'
import type { WorkflowNodeData, SendScheduleConfig, SendScheduleMode } from '@/types/workflow'
import DateTimePicker from '@/components/ui/DateTimePicker'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'
import { cn } from '@/lib/utils'
import {
  dividerClass,
  checkboxInputClass,
  checkboxLabelClass,
  sectionEyebrowClass,
  sectionEyebrowStyle,
  pillBaseClass,
  pillActiveClass,
  pillInactiveClass,
  pillTextStyle,
} from './_kit'

const DEFAULTS: SendScheduleConfig = {
  timezone_enabled: false,
  schedule_mode: 'window',
  window_start: '09:00',
  window_end: '17:00',
  target_time: '10:00',
  business_days_only: true,
  fallback_timezone: 'UTC',
}

// Same zone list the Trigger node's schedule UI offers (TriggerConfig.tsx).
const FALLBACK_TIMEZONES: { value: string; label: string }[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
]

interface SendSchedulingSectionProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

/**
 * Recipient-timezone send window config for the sendEmail node.
 * When enabled, the campaign executor holds each email until the
 * recipient's local window opens (backend: services/send_window.py).
 */
const SendSchedulingSection: React.FC<SendSchedulingSectionProps> = ({ data, updateField }) => {
  const cfg: SendScheduleConfig = { ...DEFAULTS, ...(data.send_schedule_config || {}) }
  const set = (patch: Partial<SendScheduleConfig>) =>
    updateField('send_schedule_config', { ...cfg, ...patch })

  return (
    <div>
      <div className={dividerClass} />
      <span className={sectionEyebrowClass} style={sectionEyebrowStyle}>
        Send scheduling
      </span>

      <label className={cn(checkboxLabelClass, 'mt-3 select-none')}>
        <input
          type="checkbox"
          checked={cfg.timezone_enabled}
          onChange={(e) => set({ timezone_enabled: e.target.checked })}
          className={checkboxInputClass}
        />
        <span className="text-sm text-foreground">Send in recipient&apos;s local timezone</span>
      </label>

      {cfg.timezone_enabled && (
        <div className="space-y-3 mt-3">
          <div className="flex gap-1.5">
            {([
              { value: 'window', label: 'Send within window' },
              { value: 'fixed', label: 'Send at exact time' },
            ] as { value: SendScheduleMode; label: string }[]).map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => set({ schedule_mode: m.value })}
                className={`${pillBaseClass} ${cfg.schedule_mode === m.value ? pillActiveClass : pillInactiveClass}`}
                style={pillTextStyle}
              >
                {m.label}
              </button>
            ))}
          </div>

          {cfg.schedule_mode === 'window' ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Window start" as="group">
                <DateTimePicker
                  mode="time"
                  value={cfg.window_start}
                  onChange={(v) => set({ window_start: v })}
                  ariaLabel="Send window start time"
                  placeholder="Pick time"
                />
              </Field>
              <Field label="Window end" as="group">
                <DateTimePicker
                  mode="time"
                  value={cfg.window_end}
                  onChange={(v) => set({ window_end: v })}
                  ariaLabel="Send window end time"
                  placeholder="Pick time"
                />
              </Field>
            </div>
          ) : (
            <Field label="Send at (recipient local time)" as="group">
              <DateTimePicker
                mode="time"
                value={cfg.target_time}
                onChange={(v) => set({ target_time: v })}
                ariaLabel="Exact send time (recipient local)"
                placeholder="Pick time"
              />
            </Field>
          )}

          <label className={cn(checkboxLabelClass, 'select-none')}>
            <input
              type="checkbox"
              checked={cfg.business_days_only}
              onChange={(e) => set({ business_days_only: e.target.checked })}
              className={checkboxInputClass}
            />
            <span className="text-sm text-foreground">Business days only (Mon–Fri)</span>
          </label>

          <Field label="Fallback timezone">
            {(f) => (
              <Select
                {...f}
                value={cfg.fallback_timezone}
                onChange={(value) => set({ fallback_timezone: value })}
                options={FALLBACK_TIMEZONES}
                className="w-full"
              />
            )}
          </Field>

          <p className="text-[10px] text-muted-foreground leading-relaxed">
            {cfg.schedule_mode === 'window'
              ? 'Emails due outside the window are held until it opens (spread over the first part of the window).'
              : 'Emails are held until the chosen time next occurs in the recipient\'s timezone (sent at that exact time).'}{' '}
            Each recipient&apos;s timezone comes from their contact record or is inferred
            from their country/state; the fallback applies when neither is available.
          </p>
        </div>
      )}
    </div>
  )
}

export default SendSchedulingSection
